/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect --
 * Admin email-health card calls setState inside form submit handlers
 * that call e.preventDefault() first — the experimental rule
 * misclassifies this idiomatic pattern.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Send, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Admin-facing diagnostic card for the outbound email pipeline. Surfaces
// what From/Reply-To the app is sending as, whether the Resend key is set,
// and lets any admin send a branded test message to themselves (or anyone
// they type) to confirm deliverability — including on production where
// reading logs is slow. Delivery errors are rendered verbatim so the
// "domain not verified" kind of error is obvious without leaving the UI.
export const EmailHealthSection = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toAddr, setToAddr] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadHealth = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/health");
      setHealth(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load email health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHealth(); }, []);

  const sendTest = async (e) => {
    e?.preventDefault?.();
    setSending(true);
    setLastResult(null);
    try {
      const body = toAddr.trim() ? { to: toAddr.trim().toLowerCase() } : {};
      const { data } = await api.post("/admin/email/test", body);
      setLastResult(data);
      if (data.ok) toast.success(`Email sent to ${data.destination}`);
      else toast.error(data.error || "Send failed — see details below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send test email");
    } finally { setSending(false); }
  };

  const keyOk = !!health?.resend_api_key_set;
  const publicUrlOk = !!health?.app_public_url;

  return (
    <section className="space-y-3" data-testid="admin-email-health-section">
      <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5" /> Email delivery (Resend)
      </h2>

      <div className="border border-[#D9E1E3] bg-white rounded-md p-5 space-y-4">
        {loading ? (
          <div className="text-sm text-[#6F828A]">Loading…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Row
                label="Resend API key"
                ok={keyOk}
                okText={`Set (${health?.resend_api_key_prefix || "—"}, length ${health?.resend_api_key_length})`}
                failText="Not set — emails will not send"
                testid="email-health-key"
              />
              <Row
                label="From header"
                ok
                okText={`${health?.email_from}${health?.email_from_source === "env" ? " · from env" : " · from default"}`}
                testid="email-health-from"
              />
              <Row
                label="Reply-To"
                ok
                okText={`${health?.email_reply_to}${health?.email_reply_to_source === "env" ? " · from env" : " · from default"}`}
                testid="email-health-replyto"
              />
              <Row
                label="Public app URL"
                ok={publicUrlOk}
                okText={health?.app_public_url}
                failText="Missing — reset links will be broken"
                testid="email-health-appurl"
              />
            </div>
            <form onSubmit={sendTest} className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#EEF3F4]">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Send test email to</label>
                <Input
                  type="email"
                  value={toAddr}
                  onChange={(e) => setToAddr(e.target.value)}
                  placeholder="(defaults to your own admin email)"
                  data-testid="email-health-test-to"
                />
              </div>
              <Button
                type="submit"
                disabled={sending || !keyOk}
                className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
                data-testid="email-health-test-send"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1.5" /> Send test</>)}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={loadHealth}
                className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm"
                data-testid="email-health-refresh"
              >
                Refresh
              </Button>
            </form>

            {lastResult && (
              <div
                className={`text-sm rounded-sm border px-3 py-3 ${lastResult.ok ? "bg-[#DCEFDC] border-[#A9D3A9] text-[#1E5A1E]" : "bg-[#FBEFED] border-[#E8B8B2] text-[#8C2A22]"}`}
                data-testid="email-health-test-result"
              >
                {lastResult.ok ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Delivered to Resend</div>
                      <div className="text-xs text-[#2E3A3F] mt-1">
                        Destination: <span className="font-mono-data">{lastResult.destination}</span><br />
                        Resend ID: <span className="font-mono-data break-all">{lastResult.id}</span><br />
                        Check the recipient inbox (and spam) to confirm actual delivery.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Send failed</div>
                      <div className="text-xs text-[#2E3A3F] mt-1">
                        {lastResult.skipped ? (
                          <>Reason: <span className="font-mono-data">{lastResult.reason}</span> — set <span className="font-mono-data">RESEND_API_KEY</span> in your env and redeploy.</>
                        ) : (
                          <>Error: <span className="font-mono-data">{lastResult.error}</span></>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <p className="text-[11px] text-[#6F828A] italic">
        All outbound emails (password reset, trial reminders, billing, feature-request confirmations, etc.) go through the same branded sender and Reply-To above. Every attempt is logged server-side with the Resend message id.
      </p>
    </section>
  );
};

const Row = ({ label, ok, okText, failText, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <span className={`w-4 h-4 mt-0.5 inline-flex items-center justify-center flex-shrink-0 rounded-full ${ok ? "bg-[#DCEFDC] text-[#1E5A1E]" : "bg-[#FBEFED] text-[#8C2A22]"}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    </span>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
      <div className={`font-mono-data text-[13px] break-all ${ok ? "text-[#2E3A3F]" : "text-[#8C2A22]"}`}>
        {ok ? (okText || "—") : (failText || "Not configured")}
      </div>
    </div>
  </div>
);
